import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../hooks/useBranding";
import {
  Server,
  Lock,
  Mail,
  ArrowRight,
  Eye,
  EyeOff,
  ShieldCheck,
  Zap,
  Wifi,
  Radio,
  CheckCircle2
} from "lucide-react";

export const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { login } = useAuth();
  const navigate = useNavigate();
  const branding = useBranding();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(
        err.response?.data?.detail || "Email atau kata sandi tidak cocok. Silakan coba lagi."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060a14] flex flex-col justify-center items-center p-4 relative overflow-hidden">
      {/* Background Neon Grid / Glow */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-cyan-600/15 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-blue-600/15 rounded-full blur-[128px] pointer-events-none" />

      {/* Main Container */}
      <div className="w-full max-w-md relative z-10">
        {/* Brand Card */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-tr from-cyan-500 via-blue-600 to-indigo-600 shadow-xl shadow-cyan-500/20 mb-3 border border-cyan-400/30 overflow-hidden">
            {branding.app_logo ? (
              <img src={branding.app_logo} alt="Logo Aplikasi" className="w-full h-full object-contain" data-testid="login-app-logo" />
            ) : (
              <Server className="w-7 h-7 text-white" />
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white" data-testid="login-app-name">
            {branding.app_name || "NUSA NET FIBER"}
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            {branding.app_description || "Sistem Informasi Billing & Manajemen Jaringan Internet"}
          </p>
        </div>

        {/* Login Form Card */}
        <div className="bg-[#0e1628]/90 backdrop-blur-xl border border-slate-800/90 rounded-2xl p-6 sm:p-8 shadow-2xl shadow-black/80">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-800">
            <div>
              <h2 className="text-lg font-bold text-slate-100">Portal Masuk</h2>
              <p className="text-xs text-slate-400">Silakan login untuk mengakses billing</p>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] text-emerald-400 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              RouterOS Ready
            </div>
          </div>

          {error && (
            <div
              data-testid="login-error-alert"
              className="mb-5 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs flex items-center gap-2"
            >
              <div className="w-2 h-2 rounded-full bg-rose-400" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                Email / Username Operator
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  required
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@ispgateway.id"
                  className="w-full pl-9 pr-3 py-2.5 bg-slate-900/80 border border-slate-700/80 rounded-xl text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                Kata Sandi
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-9 pr-10 py-2.5 bg-slate-900/80 border border-slate-700/80 rounded-xl text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full py-2.5 px-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-xl text-sm shadow-lg shadow-cyan-500/25 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <span>Memproses...</span>
              ) : (
                <>
                  <span>Masuk ke Sistem</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer info */}
        <div className="text-center mt-5 text-xs text-slate-500 flex items-center justify-center gap-4">
          <span>v2.6 ISP Engine</span>
          <span>•</span>
          <span>FastAPI + MongoDB</span>
          <span>•</span>
          <span>RouterOS API Ready</span>
        </div>
        <p className="text-center mt-3 text-xs text-slate-500">
          Pelanggan? <a href="/portal/login" className="text-emerald-400 hover:underline font-semibold" data-testid="link-portal-pelanggan">Masuk Portal Pelanggan →</a>
        </p>
        <div className="text-center mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-slate-500" data-testid="legal-footer-links">
          <a href="/tentang-kami" className="hover:text-cyan-300" data-testid="footer-link-tentang">Tentang Kami</a>
          <span>•</span>
          <a href="/kebijakan-privasi" className="hover:text-cyan-300" data-testid="footer-link-privasi">Kebijakan Privasi</a>
          <span>•</span>
          <a href="/syarat-ketentuan" className="hover:text-cyan-300" data-testid="footer-link-syarat">Syarat &amp; Ketentuan</a>
          <span>•</span>
          <a href="/kontak" className="hover:text-cyan-300" data-testid="footer-link-kontak">Kontak</a>
        </div>
      </div>
    </div>
  );
};
